<!-- https://amp.dev/documentation/components/amp-geo -->
<!-- Показываем рекламу адфокс для этих стран, для всех остальных гугловскую -->
<!-- Абхазия, Узбекистан, Россия Туркменистан, Киргизия, Армения, Казахстан, Беларусь, Таджикистан, Молдова, Азербайджан, Украина -->

<template>
  <NuxtLayout :name="layout">
    <div class="container">
      <component
        :is="'amp-geo'"
        layout="nodisplay"
      />

      <AmpError v-if="errorCode === 503" />

      <main
        v-else-if="article"
        class="main"
      >
        <!-- Карточка статьи -->
        <ArticleCard
          :article="article"
          :comments-count="commentsCount"
          :is-advertising="isAdvertising"
          :is-promo="isPromo"
          class="card"
        />

        <!-- Статья -->
        <article
          :style="articleStyle"
          class="content single-article__post-content"
        >
          <template
            v-for="(item, index) in article.sanitizedStructuredContent"
            :key="item.id"
          >
            <TheContent :elem="item" />

            <div
              v-if="isAmpAfterFirstParagraphWidgets(index)"
              class="widgets-area-amp widgets-area-amp--after-first-paragraph"
            >
              <div
                v-for="widget in ampAfterFirstParagraphWidgets"
                :key="widget.id"
                class="widgets-area-amp__item"
              >
                <TheContent
                  v-for="widgetItem in sanitizeAmp(widget.data)"
                  :key="widgetItem.id"
                  :elem="widgetItem"
                />
              </div>
            </div>

            <!-- Отображать рекламу через 6 абзацев -->
            <div
              v-if="adsEnabled && index % 6 === 0"
              class="amp-ad-paragraph"
            >
              <AdsAmp
                class="ad ad_small-margin"
                :yandex-params="yandexParams"
              />
            </div>
          </template>
        </article>

        <!-- Источник, Обложка, автор и тп информация о статье -->
        <ArticleNote
          :is-promo="isPromo"
          :notes="articleNotes"
          class="notes"
        />

        <div
          v-if="isPromo"
          class="create-in-container"
        >
          <p class="create-in-container__text">
            Сделано в&nbsp;
            <a
              class="create-in-container__link"
              href="/"
              >Лайфхакере</a
            >
          </p>
        </div>

        <AmpMessage
          :link="article.url"
          class="message"
        />

        <ShareBlock class="share-block" />
      </main>

      <!-- Реклама под статьей -->
      <AdsAmp
        class="ad"
        :yandex-params="yandexParams"
      />

      <!-- Комментарии -->
      <CommentsContainerAmp
        v-if="showComments && article"
        :id="article.id"
        :article-url="article.url"
        class="comments"
      />

      <!-- Рекомендованные статьи -->
      <RecommendedPostsContainer
        v-if="hideInAds && article"
        :id="article.id"
        class="recommendation amp-recommended-posts"
        next-page-hide
      />

      <!-- Аналитика -->
      <HealthAnalytics
        v-if="isHealth && article"
        :article="article"
      />
      <TheAnalytics
        v-else
        :mail-counter-script="$options.analytics.mailCounterScript"
        :ya-scripts="yaScripts"
      />
      <FullScreenGallery
        v-if="gallery.exists"
        :id="$options.imagesInArticleGalleryId"
        :images="gallery.images"
      />

      <AmpActionSheet
        v-for="refContents in ampRefContent"
        :id="getRefId(refContents.parentId)"
        :key="refContents.content.id"
        :ref-content="refContents.content"
      />
    </div>
  </NuxtLayout>
</template>

<script lang="ts">
  import { getDateText } from '@devhacker/shared/utils/date';
  import { parseData } from '@devhacker/shared/utils/parseData';
  import TheAnalytics from '~/components/Analytics/TheAnalytics.amp.vue';
  import HealthAnalytics from '~/components/HealthAnalytics.amp.vue';
  import AmpError from '~/components/AmpError.amp.vue';
  import RecommendedPostsContainer from '~/containers/RecommendedPostsContainer.amp/RecommendedPostsContainer.amp';
  import AmpMessage from '~/components/AmpMessage.amp.vue';
  import ArticleCard from '~/components/ArticleCard.amp.vue';
  import AmpActionSheet from '~/components/AmpActionSheet.amp.vue';
  import ShareBlock from '~/components/ShareBlock.amp/ShareBlock.amp.vue';
  import ArticleNote, { getArticleNoteProps } from '~/components/ArticleNote.amp.vue';
  import TheContent from '~/components/content/TheContent';
  import FullScreenGallery from '~/components/FullScreenGallery.amp/FullScreenGallery.amp.vue';
  import { getRefId } from '~/utils/content/Amp/getRefId';

  import { sanitizeAmp } from '~/libs/sanitize-amp';

  import {
    TOP_MAIL_ID,
    YA_METRIKA_FOND_ID,
    YA_METRIKA_ID,
    YA_METRIKA_ID_HEALTH,
  } from '~/constants/config';
  import type { TArticleNoteItem } from '~/types';
  import parseAmpContent from '~/libs/parseAmpContent.amp';
  import { usePageStore } from '~/store/pagesData';
  import { isVisible } from '~/libs/widgetsVisibility';

  import { isHealthPath } from '~/utils/router';
  import { yaAmpEventsConfigExtender } from '~/components/Analytics/yaAmpEvets';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import { getErrorCodeForFetch } from '~/utils/errors';
  import type { RefAmp } from '~/types/components/Ref';
  import { prepareContent } from '~/utils/content/prepareContent';
  import { FetchCommentsCountSchema } from '~/schemas/talker';
  import { useWidgetsStore } from '~/store/widgets';
  import CommentsContainerAmp from '~/components/Amp/Comments/CommentsContainer.amp.vue';
  import { useAmpCustomElement } from '@devhacker/shared/composables/useAmpCustomElement';
  import { PAGES_WITH_NO_ARTICLE_ATTRIBUTES } from '~/constants/amp';
  import { useArticleAnalyticsHead } from '~/domains/head/analytics/services/useArticleAnalyticsHead';
  import { useSeoHead } from '~/domains/head/seo/services/useSeoHead';
  import AdsAmp from '@devhacker/shared/components/ads/AdsAmp.vue';
  import { yandexAdsParams } from '@devhacker/shared/components/ads/config';

  /**
   * Данные для аналитики
   */

  const analytics = {
    YA_METRIKA_ID_HEALTH,
    mailCounterScript: `{"vars": {"id": "${TOP_MAIL_ID}"}}`,
  } as const;

  interface SingleArticleAmp extends ISingleArticle {
    sanitizedStructuredContent: TContent[];
  }

  export default defineNuxtComponent({
    name: 'SingleArticleAmp',

    analytics,

    imagesInArticleGalleryId: 'imagesInArticleFullScreenGallery',

    amp: 'only',

    components: {
      TheAnalytics,
      HealthAnalytics,
      CommentsContainerAmp,
      RecommendedPostsContainer,
      AmpMessage,
      ArticleCard,
      ShareBlock,
      ArticleNote,
      TheContent,
      FullScreenGallery,
      AmpError,
      AmpActionSheet,
      AdsAmp,
    },

    async setup() {
      definePageMeta({ layout: false });

      useAmpCustomElement('amp-geo');

      const imagesInArticleGalleryId = 'imagesInArticleFullScreenGallery';
      const errorCode = ref(-1);
      const route = useRoute();
      const nuxtApp = useNuxtApp();
      const pageStore = usePageStore();
      const widgetsStore = useWidgetsStore();
      const routeName = computed(() => route.name);
      const routePath = computed(() => route.path);
      const routeFullPath = computed(() => route.fullPath);

      const article = pageStore.currentArticle;

      const ampArticle = ref<null | SingleArticleAmp>(null);
      const gallery = ref({ exists: false, images: [''] });
      const ampRefContent = ref<RefAmp[]>([]);
      const commentsCount = ref<null | number>(null);

      if (!article) {
        throw createError({ statusCode: 404 });
      }

      provide('articleUrl', () => article.url);

      try {
        const { data: commentCountResponse, error } = useAsyncData(() =>
          nuxtApp.$talkerApi.fetchCommentsCount([article.id]),
        );

        if (error.value) {
          console.error('Error in SingleArticle.amp.vue:', error.value);
          throw error;
        }

        if (commentCountResponse.value?.status === 200) {
          const parsedResponse = parseData(
            commentCountResponse.value.data,
            FetchCommentsCountSchema,
          );
          commentsCount.value = parsedResponse.data[0].count;
        }

        const sanitizedStructuredContent = sanitizeAmp(
          prepareContent(article.structuredContent, true),
        );

        const { galleryExists, content, galleryImages, refsContents } = parseAmpContent(
          sanitizedStructuredContent,
          imagesInArticleGalleryId,
        );

        ampArticle.value = {
          ...article,
          sanitizedStructuredContent: content,
          content: [],
          structuredContent: [],
        };

        gallery.value.exists = galleryExists;
        gallery.value.images = galleryImages;
        ampRefContent.value = refsContents;
      } catch (error) {
        errorCode.value = getErrorCodeForFetch(error);
        // this.$sentry.captureException(error);

        console.error('SingleArticle.amp fetch', error);
        throw createError({ statusCode: 503 });
      }

      useSeoHead(computed(() => pageStore.currentSeo!));
      useArticleAnalyticsHead(article, pageStore.currentSeo);
      useHead({
        bodyAttrs: {
          class: 'amp-geo-pending',
        },
      });

      return {
        article: ampArticle,
        gallery,
        ampRefContent,
        commentsCount,
        errorCode,
        pageStore,
        routeFullPath,
        routeName,
        routePath,
        widgetsStore,
      };
    },

    computed: {
      layout() {
        return this.isHealth ? 'health-amp' : 'default-amp';
      },

      isHealth(): boolean {
        return isHealthPath(this.routePath);
      },

      metricIds: () => [YA_METRIKA_ID, YA_METRIKA_FOND_ID],

      yandexParams() {
        return {
          ...yandexAdsParams,
          'data-adfox-params': this.isHealth
            ? '{"p1": "dkjbt", "p2": "fqzb"}'
            : '{"p1": "cdqnt", "p2": "fqzb"}',
        };
      },

      ampAfterFirstParagraphWidgets(): TWidget[] {
        const { article } = this;

        if (!article) {
          return [];
        }

        const currentVisibilityData: VisibilityCondition[] =
          this.pageStore.getVisibilityDataForArticle(article, this.routeName);

        return this.widgetsStore.currentAmpAfterFirstParagraphWidgets.filter((widget: TWidget) =>
          isVisible(currentVisibilityData, widget.visibility),
        );
      },

      /** Содержимое скрипта метрик */
      yaScripts(): string[] {
        return this.metricIds.map((item): string => {
          const baseScript = { vars: { counterId: item }, triggers: {} };

          // Добавляем кастомные события только для счетчика лх
          if (item === YA_METRIKA_ID) {
            return yaAmpEventsConfigExtender(
              baseScript,
              getSlugFromUrl(this.routeFullPath),
              this.article,
            );
          }

          // В амп подключаются несколько счетчиков для отслеживания посещения
          return JSON.stringify(baseScript);
        });
      },

      firstParagraphIndex(): number | undefined {
        if (!this.article?.sanitizedStructuredContent) {
          return;
        }

        for (let index = 0; index < this.article?.sanitizedStructuredContent?.length; index++) {
          if (this.article?.sanitizedStructuredContent[index]?.type === 'p') {
            return index;
          }
        }

        return 0;
      },

      /**
       * Данные для отображения под статьей.
       *
       * @return Пропсы для компонента ArticleNote
       */
      articleNotes(): TArticleNoteItem[] {
        if (!this.article) {
          return [];
        }

        const {
          meta: { customAuthors, source, cover_source, adsSource },
          isAdvertising,
          isPromo,
          advertLabel,
          caption: legacy_cover_source,
        } = this.article;

        const coverSourceWithFallback: IArticleMeta['cover_source'] = {
          ...cover_source,
          text: cover_source.text || legacy_cover_source,
        };

        return getArticleNoteProps(
          source,
          coverSourceWithFallback,
          customAuthors,
          'Над текстом работали:',
          adsSource,
          isAdvertising,
          isPromo,
          advertLabel,
        );
      },

      /**
       * Свойство, отвечающее за видимость рекламы.
       * Реклама отображается если у статьи нет тегов `noad` и `noadsense`.
       *
       * @return реклама включена?
       */
      adsEnabled(): boolean {
        const { article } = this;
        if (!article) {
          return false;
        }

        const noAdsTag = article.tags.find((item: Tag): boolean => {
          return ['noad', 'noadsense'].includes(item.title);
        });
        return noAdsTag === undefined;
      },

      isJumperPost(): boolean {
        return this.article?.meta?.jumperScroller?.isActive ?? false;
      },

      showComments(): boolean {
        const isNotJumperPost = !this.isJumperPost;
        const commentsEnabledIsTrue = this.article?.commentsEnabled === true;

        return commentsEnabledIsTrue && isNotJumperPost;
      },

      /**
       * @return шаблон статьи промо?
       */
      isPromo(): boolean {
        return this.article?.isPromo ?? false;
      },

      /**
       * @return статья партнерская??
       */
      isAdvertising(): boolean {
        return this.article?.isAdvertising ?? false;
      },

      /**
       * @return стили статьи. Можно менять глобальные css переменные, как --main-color.
       */
      articleStyle() {
        const mainColor = this.article?.meta?.mainColor;

        return mainColor
          ? {
              '--main-color': mainColor,
            }
          : null;
      },

      hideInAds(): boolean | undefined {
        return Boolean(this.article) && !PAGES_WITH_NO_ARTICLE_ATTRIBUTES.includes(this.routePath);
      },
    },

    methods: {
      getRefId,
      getDateText,
      sanitizeAmp,
      isAmpAfterFirstParagraphWidgets(index: number): boolean {
        return (
          index === this.firstParagraphIndex &&
          this.ampAfterFirstParagraphWidgets.length > 0 &&
          this.article?.template !== 'promo' &&
          this.article?.template !== 'broadside' &&
          !this.article?.isPromo
        );
      },
    },
  });
</script>

<style lang="scss">
  @use '../scss/article.amp';
</style>

<style lang="scss" scoped>
  .container {
    @include margin-x(auto);

    max-width: 600px;

    .main {
      background: white;
      border: 1px solid $borderColor;
      padding: 16px 0 0px;
      margin-bottom: 16px;
    }

    .message {
      @include margin-x(auto);

      width: calc(100% - 32px);
    }

    .create-in-container {
      font-family: $mainFont;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;

      text-align: center;

      padding: 16px 0 16px;
      margin-top: 16px;
      border: 1px solid #e7e7e7;

      .create-in-container__text {
        color: #000;
      }

      .create-in-container__link {
        text-decoration: none;

        @include hover {
          text-decoration: underline;
        }

        color: var(--main-color);

        &:visited {
          color: #7142d6;
        }
      }
    }
  }

  .ad {
    margin: 16px auto;
    justify-content: center;
    display: flex;
    background: white;

    &_small-margin {
      margin-left: 0;
      margin-right: 0;
      @include margin-y(8px);
    }
  }

  .recommendation,
  .comments {
    margin-bottom: 16px;
  }

  .card {
    margin-bottom: 16px;
    margin-left: -1px;
    width: calc(100% + 2px);
  }

  .content {
    padding: 32px 16px;
    overflow: hidden;
  }

  .notes {
    margin: 32px 16px 24px;
  }

  .share-block {
    margin-bottom: 24px;
  }
</style>
